/**
 * TA v4.3 — Response field with telemetry capture.
 *
 * Per TCA QA Protocol v2.0 §4, we capture:
 *   - response_field_id, text, completion_seconds
 *   - paste_events: [{at, chars}]
 *   - focus_events: [{at, type: 'focus'|'blur'}]
 *   - time_to_first_keystroke_ms
 *   - keystroke_count, word_count
 *
 * The component owns the telemetry record (via ref) and notifies parent on every change
 * via `onChange(text, telemetry)`. Parent flushes to /save on debounce.
 *
 * `type: "code"` uses a Monaco editor; `type: "long_text"` uses a textarea.
 *
 * Props:
 *   field             — the response_field object from the JSON
 *   itemId            — question.id (for telemetry attribution)
 *   value             — current text
 *   initialTelemetry  — prior telemetry (when resuming after refresh)
 *   onChange(text, telemetry) — fires on every change
 *   readOnly          — if true, renders text non-editable (used for chained Part A after submit)
 *   language          — language for Monaco when type=code
 *   labelOverride     — optional label override (used by Phase 3 "EMAIL DRAFT")
 *   headerHint        — small badge text (e.g., "EMAIL DRAFT")
 *   minWordsHint      — soft hint for min words (Phase 3 communication)
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { wordCount } from "./taHelpers";

const TEXTAREA_CLASS = "w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none resize-y";

const EDIT_OPTIONS_BASE = {
    minimap: { enabled: false },
    fontSize: 13,
    fontFamily: "JetBrains Mono, ui-monospace, monospace",
    fontLigatures: false,
    lineNumbers: "on",
    folding: false,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    wordWrap: "on",
    tabSize: 2,
    renderLineHighlight: "none",
};

export default function TAResponseField({
    field,
    itemId,
    value,
    initialTelemetry,
    onChange,
    readOnly = false,
    language = "plaintext",
    labelOverride = null,
    headerHint = null,
    minWordsHint = null,
}) {
    const tRef = useRef(
        initialTelemetry || {
            response_field_id: field.id,
            item_id: itemId,
            text: value || "",
            completion_seconds: 0,
            time_to_first_keystroke_ms: null,
            paste_events: [],
            focus_events: [],
            keystroke_count: 0,
            word_count: wordCount(value || ""),
        }
    );
    const mountedAt = useRef(Date.now());
    const [, force] = useState(0);  // for word counter

    const max = field.max_words || 0;

    const emit = useCallback((newText) => {
        const t = tRef.current;
        t.text = newText;
        t.completion_seconds = (Date.now() - mountedAt.current) / 1000;
        t.word_count = wordCount(newText);
        onChange(newText, { ...t, paste_events: [...t.paste_events], focus_events: [...t.focus_events] });
        force((n) => n + 1);
    }, [onChange]);

    const handleType = (next) => {
        if (readOnly) return;
        const t = tRef.current;
        t.keystroke_count = (t.keystroke_count || 0) + 1;
        if (t.time_to_first_keystroke_ms == null) {
            t.time_to_first_keystroke_ms = Date.now() - mountedAt.current;
        }
        emit(next);
    };

    const handleFocus = () => {
        tRef.current.focus_events.push({ at: new Date().toISOString(), type: "focus" });
    };
    const handleBlur = () => {
        tRef.current.focus_events.push({ at: new Date().toISOString(), type: "blur" });
        emit(tRef.current.text);  // flush latest
    };
    const handlePaste = (e) => {
        const chars = (e.clipboardData?.getData?.("text") || "").length;
        tRef.current.paste_events.push({ at: new Date().toISOString(), chars });
        // text update will be captured by the subsequent onChange
    };

    // Word counter colours
    const wc = wordCount(value || "");
    const overLimit = max > 0 && wc > max;
    const minOk = !minWordsHint || wc >= minWordsHint;

    const headerLabel = labelOverride || field.label || "Response";

    return (
        <div className={`space-y-1.5 ${readOnly ? "opacity-60" : ""}`} data-testid={`ta-response-field-${field.id}`}>
            <div className="flex items-baseline justify-between">
                <div className="flex items-center gap-2">
                    <label className={`block ${readOnly ? "text-[11px]" : "text-xs"} uppercase tracking-wider font-mono text-gray-400`}>
                        {headerLabel}
                    </label>
                    {headerHint && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 text-amber-200 font-mono uppercase tracking-wider" data-testid={`ta-field-hint-${field.id}`}>
                            {headerHint}
                        </span>
                    )}
                    {readOnly && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-500/10 border border-gray-500/30 text-gray-400 font-mono uppercase tracking-wider">
                            submitted · read only
                        </span>
                    )}
                </div>
                <div className={`text-[11px] font-mono ${overLimit ? "text-red-300" : minWordsHint && !minOk ? "text-amber-300" : "text-gray-500"}`} data-testid={`ta-wordcount-${field.id}`}>
                    {wc}{max > 0 ? ` / ${max} words` : " words"}
                    {minWordsHint && !minOk && ` · min ${minWordsHint}`}
                </div>
            </div>

            {field.type === "code" ? (
                <div className="border border-border/40 bg-[#0f0f0f]">
                    <Editor
                        height={readOnly ? 140 : 220}
                        language={language || "plaintext"}
                        value={value || ""}
                        theme="vs-dark"
                        onChange={(v) => handleType(v ?? "")}
                        onMount={(editor) => {
                            editor.onDidFocusEditorWidget(handleFocus);
                            editor.onDidBlurEditorWidget(handleBlur);
                            editor.onDidPaste((ev) => {
                                // Monaco doesn't expose pasted text length directly; approximate
                                tRef.current.paste_events.push({
                                    at: new Date().toISOString(),
                                    chars: (editor.getModel()?.getValueLength() || 0),
                                    via: "monaco",
                                });
                            });
                        }}
                        options={{ ...EDIT_OPTIONS_BASE, readOnly }}
                    />
                </div>
            ) : (
                <textarea
                    value={value || ""}
                    onChange={(e) => handleType(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onPaste={handlePaste}
                    readOnly={readOnly}
                    placeholder="Your response…"
                    className={`${TEXTAREA_CLASS} ${headerHint ? "h-44" : "h-28"}`}
                    data-testid={`ta-textarea-${field.id}`}
                />
            )}
        </div>
    );
}
